import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Photo from "./Photo";
import { Breadcrumb } from "./ServiceSections";

/**
 * Editorial layout for legal pages (Privacy, Terms, etc.).
 * Smooth-scrolling section anchors + sticky in-page TOC on desktop.
 *
 * Props:
 *  - eyebrow: small label
 *  - title: hero title (JSX allowed)
 *  - lastUpdated: ISO-ish date string
 *  - intro: short paragraph
 *  - sections: [{ id, title, body: JSX }]
 *  - breadcrumb: string
 */
export const LegalPageLayout = ({ eyebrow, title, intro, lastUpdated, sections, breadcrumb }) => {
  useEffect(() => {
    // Scroll to top whenever this layout mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden bg-ink">
        <div className="absolute inset-0 opacity-25">
          <Photo slug="hero-banquet-01" alt="" ratio="auto" className="!relative h-full w-full" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/70 to-ink" />
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />

        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
          <Breadcrumb current={breadcrumb} />
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
            {eyebrow}
          </p>
          <h1
            className="font-display tracking-editorial leading-[0.95]"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
          >
            {title}
          </h1>
          <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-bone/80">
            {intro}
          </p>
          {lastUpdated && (
            <p className="mt-8 text-[10px] uppercase tracking-wider2 font-bold text-bone/45">
              Last updated · {lastUpdated}
            </p>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 lg:gap-16">
            {/* Sticky TOC */}
            <aside className="hidden lg:block col-span-4 xl:col-span-3">
              <div className="sticky top-32">
                <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-5">
                  On this page
                </p>
                <ul className="space-y-2.5 border-l border-ink/15">
                  {sections.map((s, i) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="block pl-5 -ml-px py-1 text-sm text-char/75 hover:text-ember hover:border-ember border-l border-transparent transition-colors"
                      >
                        <span className="font-sans font-bold text-ember mr-2">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 pt-10 border-t border-ink/15">
                  <Link to="/contact" className="group inline-flex items-center gap-2 text-xs uppercase tracking-wider2 font-bold text-ember hover:text-ink transition-colors">
                    Questions? Contact us
                    <ArrowUpRight size={14} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </div>
            </aside>

            {/* Sections */}
            <article className="col-span-12 lg:col-span-8 xl:col-span-9 space-y-16 lg:space-y-20">
              {sections.map((s, i) => (
                <section key={s.id} id={s.id} className="scroll-mt-28">
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className="font-sans font-bold text-sm text-ember tracking-tight">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-display tracking-editorial leading-tight" style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}>
                      {s.title}
                    </h2>
                  </div>
                  <div className="prose-legal text-char text-base md:text-lg leading-relaxed space-y-5 max-w-2xl">
                    {s.body}
                  </div>
                </section>
              ))}
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/**
 * Light typography helpers for use inside `body` JSX.
 */
export const P = ({ children }) => <p>{children}</p>;
export const Ul = ({ items }) => (
  <ul className="space-y-2 pl-4 list-disc marker:text-ember/80">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);
export const Strong = ({ children }) => <strong className="text-ink font-bold">{children}</strong>;

export default LegalPageLayout;
